import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

// Messenger inbox controller.
//
// Ported from ArtisanAVL app/javascript/controllers/customer_communications_controller.js:
// - broadcast-then-refetch: the socket only says "changed"; the inbox is
//   refetched from the authorized HTTP endpoint and swapped in place;
// - catch-up on reconnect / tab resume / online, so a missed signal is
//   recovered by the next fetch;
// - scroll preservation and a reader-controlled "new message" indicator
//   (never auto-scrolls away from what the reader is looking at).
export default class extends Controller {
  static targets = ["frame", "scroller", "indicator", "live"]
  static values = { url: String, channel: String, readUrl: String, params: Object }

  connect() {
    this.onResume = () => { if (document.visibilityState !== "hidden") this.refresh() }
    window.addEventListener("online", this.onResume)
    window.addEventListener("pageshow", this.onResume)
    document.addEventListener("visibilitychange", this.onResume)
    this.toBottom()
    this.consumer = createConsumer()
    this.subscription = this.consumer.subscriptions.create(
      { ...this.paramsValue, channel: this.channelValue },
      {
        connected: () => { this.setLive("Live updates on"); this.refresh() },
        disconnected: () => this.setLive("Reconnecting…"),
        rejected: () => this.setLive("Live updates unavailable"),
        received: (data) => { if (data?.type === "changed") this.refresh() }
      }
    )
  }

  disconnect() {
    window.removeEventListener("online", this.onResume)
    window.removeEventListener("pageshow", this.onResume)
    document.removeEventListener("visibilitychange", this.onResume)
    this.subscription?.unsubscribe()
    this.consumer?.disconnect()
  }

  setLive(text) {
    if (this.hasLiveTarget) this.liveTarget.textContent = text
  }

  atBottom() {
    if (!this.hasScrollerTarget) return true
    const s = this.scrollerTarget
    return s.scrollHeight - s.scrollTop - s.clientHeight < 80
  }

  toBottom() {
    if (this.hasScrollerTarget) this.scrollerTarget.scrollTop = this.scrollerTarget.scrollHeight
  }

  async refresh() {
    if (this.inflight) { this.pending = true; return }
    this.inflight = true
    try {
      const wasAtBottom = this.atBottom()
      const top = this.hasScrollerTarget ? this.scrollerTarget.scrollTop : 0
      const before = this.element.querySelectorAll("[data-message-id]").length
      const composer = this.element.querySelector("textarea[name=body]")
      const draft = composer?.value
      const focused = document.activeElement === composer
      const response = await fetch(this.urlValue, { headers: { Accept: "text/html" }, credentials: "same-origin", cache: "no-store" })
      if (!response.ok) return
      this.frameTarget.innerHTML = await response.text()
      const newComposer = this.element.querySelector("textarea[name=body]")
      if (newComposer && draft) newComposer.value = draft
      if (newComposer && focused) newComposer.focus()
      const after = this.element.querySelectorAll("[data-message-id]").length
      if (wasAtBottom) {
        this.toBottom()
      } else if (this.hasScrollerTarget) {
        this.scrollerTarget.scrollTop = top
        if (after > before && this.hasIndicatorTarget) this.indicatorTarget.hidden = false
      }
    } catch (_error) {
      // The next signal or resume performs another authorized fetch.
    } finally {
      this.inflight = false
      if (this.pending) { this.pending = false; this.refresh() }
    }
  }

  jump() {
    this.toBottom()
    if (this.hasIndicatorTarget) this.indicatorTarget.hidden = true
  }
}
